import React, { useEffect, useState } from 'react';
import { getAttendance } from '../../services/api';
import ResponsiveTable from '../shared/ResponsiveTable';

const AttendancePage = ({ user }) => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(''); // YYYY-MM-DD format
  const [filterType, setFilterType] = useState('all'); // 'all', 'login', 'logout'

  useEffect(() => {
    loadAttendanceData();
  }, [user.id, selectedDate, filterType]);

  const loadAttendanceData = async () => {
    try {
      setLoading(true);
      
      let res;
      if (selectedDate) {
        // Fetch records for the specific selected date
        res = await getAttendance(user.id, selectedDate, selectedDate);
      } else {
        // Fetch current month's records if no date is selected
        const currentDate = new Date();
        const year = currentDate.getFullYear();
        const month = String(currentDate.getMonth() + 1).padStart(2, '0');
        const startDate = `${year}-${month}-01`;
        const endDate = new Date(year, currentDate.getMonth() + 1, 0).toISOString().split('T')[0];
        res = await getAttendance(user.id, startDate, endDate);
      }
      
      // Filter records based on type if needed
      let filteredRecords = res.data || [];
      if (filterType !== 'all') {
        filteredRecords = filteredRecords.filter(record => record.type === filterType);
      }
      
      setRecords(filteredRecords);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDateChange = (e) => {
    setSelectedDate(e.target.value);
  };
  
  const handleFilterChange = (e) => {
    setFilterType(e.target.value);
  };
  
  const clearDateSelection = () => {
    setSelectedDate('');
  };

  const renderRow = (record, isMobile) => {
    if (isMobile) {
      return (
        <>
          <div className="flex justify-between items-center mb-2">
            <span className="font-medium text-gray-500">Type</span>
            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${record.type === 'login' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
              {record.type === 'login' ? 'Clock In' : 'Clock Out'}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="font-medium text-gray-500">Date & Time</span>
            <span className="text-gray-900">
              <div>{new Date(record.timestamp).toLocaleDateString()}</div>
              <div className="text-xs text-gray-400">{new Date(record.timestamp).toLocaleTimeString([], {hour:'2-digit', minute:'2-digit'})}</div>
            </span>
          </div>
        </>
      );
    }

    return (
      <>
        <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900"><span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${record.type === 'login' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>{record.type === 'login' ? 'Clock In' : 'Clock Out'}</span></td>
        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
          <div>{new Date(record.timestamp).toLocaleDateString()}</div>
          <div className="text-xs text-gray-400">{new Date(record.timestamp).toLocaleTimeString([], {hour:'2-digit', minute:'2-digit'})}</div>
        </td>
      </>
    );
  };

  // Calculate summary statistics
  const loginCount = records.filter(record => record.type === 'login').length;
  const logoutCount = records.filter(record => record.type === 'logout').length;

  return (
    <div className="max-w-4xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Attendance Records</h1>
        <div className="mt-4 sm:mt-0 flex flex-col sm:flex-row sm:space-x-4 space-y-4 sm:space-y-0">
          <div>
            <label htmlFor="date" className="block text-sm font-medium text-gray-700 mb-1">Select Date</label>
            <div className="flex space-x-2">
              <input
                type="date"
                id="date"
                value={selectedDate}
                onChange={handleDateChange}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
              />
              <button
                onClick={clearDateSelection}
                className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                Clear
              </button>
            </div>
          </div>
          <div>
            <label htmlFor="filter" className="block text-sm font-medium text-gray-700 mb-1">Filter By</label>
            <select
              id="filter"
              value={filterType}
              onChange={handleFilterChange}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
            >
              <option value="all">All Records</option>
              <option value="login">Clock In Only</option>
              <option value="logout">Clock Out Only</option>
            </select>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-3 mb-6">
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0 bg-green-500 rounded-md p-2">
                <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="ml-3 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Total Logins</dt>
                  <dd className="flex items-baseline">
                    <div className="text-2xl font-semibold text-gray-900">{loginCount}</div>
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0 bg-red-500 rounded-md p-2">
                <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="ml-3 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Total Logouts</dt>
                  <dd className="flex items-baseline">
                    <div className="text-2xl font-semibold text-gray-900">{logoutCount}</div>
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0 bg-blue-500 rounded-md p-2">
                <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <div className="ml-3 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Total Records</dt>
                  <dd className="flex items-baseline">
                    <div className="text-2xl font-semibold text-gray-900">{records.length}</div>
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <ResponsiveTable
          headers={[ 'Type', 'Date & Time' ]}
          data={records}
          renderRow={renderRow}
          emptyMessage={selectedDate ? `No attendance records found for ${selectedDate}` : "No attendance records found for the current month"}
          loading={loading}
        />
      </div>
    </div>
  );
};

export default AttendancePage;